import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import './App.css';
import { getOktaAuthConfig } from './config/oktaConfig';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { configureApiAuth } from './utils/api';
import SecureRoute from './components/SecureRoute';
import Login from './components/Login';
import LoginCallback from './components/LoginCallback';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import FileUpload from './components/FileUpload';
import MonitoringDashboard from './components/MonitoringDashboard';
import TitleFileUpload from './components/TitleFileUpload';
import TitleMonitoringDashboard from './components/TitleMonitoringDashboard';
import CustomDocumentProcessor from './components/CustomDocumentProcessor';
import SemanticSearch from './components/SemanticSearch/SemanticSearch';
import AIChatbot from './components/AIChatbot';
import FloatingChatWidget from './components/FloatingChatWidget';

// Initialize Okta Auth
const oktaAuth = new OktaAuth(getOktaAuthConfig());

// Component to configure API authentication when user logs in
function ApiAuthConfigurator() {
  const { isAuthenticated, getAccessToken } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      // Configure API clients to automatically include Okta access token
      configureApiAuth(getAccessToken);
      console.log('API authentication configured');
    }
  }, [isAuthenticated, getAccessToken]);

  return null;
}

function App() {
  const [insuranceUploadedFiles, setInsuranceUploadedFiles] = useState([]);
  const [titleUploadedFiles, setTitleUploadedFiles] = useState([]);

  const handleInsuranceUploadComplete = (fileInfo) => {
    setInsuranceUploadedFiles(prev => [...prev, fileInfo]);
  };

  const handleTitleUploadComplete = (fileInfo) => {
    setTitleUploadedFiles(prev => [...prev, fileInfo]);
  };

  // Restore original URI after login
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    window.location.replace(
      toRelativeUrl(originalUri || '/', window.location.origin)
    );
  };

  return (
    <Router>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <AuthProvider>
          <ApiAuthConfigurator />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/login/callback" element={<LoginCallback />} />

            {/* Protected Routes */}
            <Route path="/*" element={
              <SecureRoute>
                <div className="App">
                  <Sidebar />
                  <main className="main-content">
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      
                      {/* Insurance Routes */}
                      <Route 
                        path="/insurance/upload" 
                        element={<FileUpload onUploadComplete={handleInsuranceUploadComplete} />} 
                      />
                      <Route 
                        path="/insurance/monitor" 
                        element={<MonitoringDashboard uploadedFiles={insuranceUploadedFiles} />} 
                      />
                      
                      {/* Title Routes */}
                      <Route 
                        path="/title/upload" 
                        element={<TitleFileUpload onUploadComplete={handleTitleUploadComplete} />} 
                      />
                      <Route 
                        path="/title/monitor" 
                        element={<TitleMonitoringDashboard uploadedFiles={titleUploadedFiles} />} 
                      />

                      {/* Custom Document Processor */}
                      <Route 
                        path="/custom-processor" 
                        element={<CustomDocumentProcessor />} 
                      />

                      {/* Semantic Search */}
                      <Route 
                        path="/search" 
                        element={<SemanticSearch />} 
                      />

                      {/* AI Chatbot Assistant */}
                      <Route 
                        path="/chatbot" 
                        element={<AIChatbot />} 
                      />

                      {/* Legacy Routes - Redirect to Insurance */}
                      <Route path="/upload" element={<Navigate to="/insurance/upload" replace />} />
                      <Route path="/monitor" element={<Navigate to="/insurance/monitor" replace />} />
                    </Routes>
                  </main>
                  
                  {/* Floating Chat Widget - Available on all pages */}
                  <FloatingChatWidget />
                </div>
              </SecureRoute>
            } />
          </Routes>
        </AuthProvider>
      </Security>
    </Router>
  );
}

export default App;

